import classNames from 'classnames';

export type ButtonVariant =
  | 'primary'
  | 'primary-thin'
  | 'pill'
  | 'square'
  | 'link'
  | 'link-no-underline'
  | 'animated-border'
  | 'custom';

export type ButtonColour =
  | 'primary'
  | 'pink'
  | 'black'
  | 'grey'
  | 'light-grey'
  | 'light-grey-salmon'
  | 'white'
  | 'white-salmon'
  | 'transparent-black'
  | 'transparent-grey'
  | 'black-outline'
  | 'grey-outline'
  | 'grey-outline-salmon'
  | 'white-outline'
  | 'white-outline-translucent'
  | 'salmon-outline'
  | 'salmon-outline-filled'
  | 'blue-green'
  | 'custom';

export const getBtnClass = (
  variant: ButtonVariant,
  colour: ButtonColour,
  withGap?: boolean,
) => {
  const isLink = variant.includes('link');

  const btnClass = classNames(
    'group transition-all',

    // variant
    {
      'flex items-center justify-center px-3 py-1.5 rounded-md':
        variant === 'primary' || variant === 'primary-thin',
      'min-w-32 h-[var(--form-el-height)]': variant === 'primary',
      'h-10': variant === 'primary-thin',

      'flex items-center justify-center h-[var(--form-el-height)] text-lg rounded-full':
        variant === 'pill',

      'flex items-center justify-center h-10 aspect-square p-1.5 rounded-md':
        variant === 'square',

      'inline-block underline': variant === 'link',
      'inline-block no-underline hover:underline':
        variant === 'link-no-underline',

      'flex items-center justify-center relative p-[2px] rounded-md':
        variant === 'animated-border',

      'gap-2': withGap,
    },

    // colour
    isLink
      ? {
          'text-primary-900': colour === 'primary',
          'text-white': colour === 'white',
          'text-black hover:text-primary-900': colour === 'black',
          'hover:opacity-60':
            variant === 'link' && ['primary', 'white'].includes(colour),
        }
      : {
          // filled
          'bg-primary-900 text-white': colour === 'primary',
          'bg-primary-50 text-primary-900 border border-primary-translucent border-solid data-[enabled=true]:hover:border-primary-900':
            colour === 'pink',
          'bg-black text-white': colour === 'black',
          'bg-gray-500 text-white': colour === 'grey',
          'bg-grey-100 text-black data-[enabled=true]:hover:bg-grey-200':
            colour === 'light-grey',
          'bg-grey-100 text-primary-900 data-[enabled=true]:hover:bg-gradient-primary-bottom data-[enabled=true]:hover:text-white':
            colour === 'light-grey-salmon',
          'bg-white text-black ': colour === 'white',
          'bg-white text-primary-900': colour === 'white-salmon',

          // transparent
          'hover:bg-black data-[enabled=true]:hover:text-white':
            colour === 'transparent-black',
          'text-black hover:bg-grey-100': colour === 'transparent-grey',

          // outline
          'border border-solid': colour.includes('outline'),
          'border-black data-[enabled=true]:hover:bg-black data-[enabled=true]:hover:text-white':
            colour === 'black-outline',
          'border-grey-200 data-[enabled=true]:hover:bg-grey-100':
            colour === 'grey-outline',
          'border-grey-900 data-[enabled=true]:hover:border-primary-900 hover:text-primary-900':
            colour === 'grey-outline-salmon',
          'border-white text-white data-[enabled=true]:hover:bg-white data-[enabled=true]:hover:text-black':
            colour === 'white-outline',
          'border-white text-white data-[enabled=true]:hover:bg-white-overlay-500':
            colour === 'white-outline-translucent',
          'border-primary-900 text-primary-900 data-[enabled=true]:hover:bg-primary-translucent':
            colour === 'salmon-outline',
          'border-primary-900 text-primary-900 data-[enabled=true]:hover:bg-primary-900 data-[enabled=true]:hover:text-white':
            colour === 'salmon-outline-filled',

          'bg-blue-green text-white': colour === 'blue-green',
        },

    // hover opacity
    !isLink &&
      ([
        'primary',
        'black',
        'grey',
        'white',
        'white-salmon',
        'blue-green',
      ].includes(colour)
        ? 'data-[enabled=true]:hover:opacity-80'
        : 'data-[enabled=true]:hover:opacity-100'),

    // disabled opacity
    ['primary', 'black', 'grey'].includes(colour)
      ? 'disabled:opacity-65'
      : 'disabled:opacity-40',
  );

  return btnClass;
};
